import {
  APPLY_CHUNK_SIZE,
  roleProvisioningService,
  type ApplyRow,
  type ApplyRowResult,
  type ScanRow,
} from "../../../../api/roleProvisioning.service.js";

/**
 * Everything a chunked run produced: the per-row results that came back, and
 * the failure that ended it early — or null for a run that sent every chunk.
 * A partial run's results are the most valuable thing it has (applied.csv, the
 * undo list, is built from them and stored nowhere else), so they are returned
 * alongside the failure rather than being thrown away with it.
 */
export type ChunkedApplyOutcome = {
  results: ApplyRowResult[];
  failure: string | null;
};

/**
 * Turns the ticked scan rows into apply rows (migration-plan.md §5). An insert
 * and an account-owner row both become `kind: "insert"` — the owner row is the
 * explicit audit row the operator opted into; a create row carries the email,
 * the Address Book contact's name and the organisation the AB resolved.
 * Rows the scan left without the ids the endpoint needs are dropped: they
 * cannot be applied, and a stale selection must not turn into a 400.
 */
export function buildApplyRows(scanRows: ScanRow[], selected: ReadonlySet<number>): ApplyRow[] {
  const rows: ApplyRow[] = [];
  for (const row of scanRows) {
    if (!selected.has(row.lineNumber) || row.organisationId === null) {
      continue;
    }
    if (row.outcome === "insert" || row.outcome === "covered_by_account_owner") {
      if (row.userId !== null) {
        rows.push({
          lineNumber: row.lineNumber,
          kind: "insert",
          userId: row.userId,
          organisationId: row.organisationId,
        });
      }
    } else if (row.outcome === "create_and_insert" && row.addressBook) {
      rows.push({
        lineNumber: row.lineNumber,
        kind: "create",
        email: row.email,
        name: row.addressBook.contactName,
        organisationId: row.organisationId,
      });
    }
  }
  return rows;
}

/**
 * Runs the rows through the apply endpoint in chunks of APPLY_CHUNK_SIZE,
 * sequentially, banking every chunk that comes back.
 *
 * Deliberately resolves rather than throws: a chunk that fails stops the run,
 * and the caller still gets everything the earlier chunks returned.
 */
export async function runChunkedApply(
  roles: string[],
  rows: ApplyRow[],
  onProgress: (done: number, total: number) => void,
): Promise<ChunkedApplyOutcome> {
  const results: ApplyRowResult[] = [];
  onProgress(0, rows.length);

  for (let start = 0; start < rows.length; start += APPLY_CHUNK_SIZE) {
    const chunk = rows.slice(start, start + APPLY_CHUNK_SIZE);
    try {
      const response = await roleProvisioningService.apply(roles, chunk);
      results.push(...response.results);
    } catch (applyError) {
      const message = applyError instanceof Error ? applyError.message : String(applyError);
      // A request that failed may still have written rows: a gateway timeout
      // cuts the connection, not the transaction the API is part-way through.
      return {
        results,
        failure:
          `Stopped after ${results.length.toLocaleString()} of ` +
          `${rows.length.toLocaleString()} rows: ${message} ` +
          "Rows in the failed batch may or may not have been written — " +
          "re-scan before retrying.",
      };
    }
    onProgress(Math.min(start + chunk.length, rows.length), rows.length);
  }

  return { results, failure: null };
}
